// src/components/auth/AuthDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Divider,
  Paper,
  Avatar,
  CircularProgress,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import { registerUser, loginUser, signInWithGoogle } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

export default function AuthDialog({ open, onClose }) {
  const theme = useTheme();
  const { resetMessageCount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError]               = useState(null);
  const [loading, setLoading]           = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleTabChange = (_e, newVal) => {
    setActiveTab(newVal);
    setError(null);
  };

  const validateForm = () => {
    setError(null);
    if (!email) {
      setError('Email is required');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (activeTab === 1 && password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await loginUser(email, password);
      resetMessageCount();
      onClose();
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err.code || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await registerUser(email, password);
      resetMessageCount();
      onClose();
    } catch (err) {
      console.error('Register error:', err);
      setError(getErrorMessage(err.code || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const user = await signInWithGoogle();
      console.log("Google sign-in successful in dialog");
      resetMessageCount();
      onClose();
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.message.includes("Browser not supported")) {
        setError("Google sign-in is not supported in this browser environment. Please try a standard browser like Chrome, Firefox, or Edge.");
      } else {
        setError(getErrorMessage(err.code || err.message));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const getErrorMessage = (code) => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please sign in.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/weak-password':
        return 'Password is too weak. Use at least 6 characters.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/redirect-cancelled-by-user':
        return 'Sign-in was cancelled. Please try again.';
      case 'auth/redirect-operation-pending':
        return 'Another sign-in operation is already in progress.';
      default:
        return code || 'An authentication error occurred.';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 0) handleLogin();
    else handleRegister();
  };

  return (
    <Dialog
      open={open}
      onClose={loading || googleLoading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: 'hidden' }
      }}
    >
      <Paper elevation={0} sx={{ position: 'relative' }}>
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            py: 4,
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mx: 'auto',
              mb: 2,
              bgcolor: 'white',
              color: 'primary.main'
            }}
          >
            {activeTab === 0 ? (
              <LoginIcon fontSize="large" />
            ) : (
              <PersonAddIcon fontSize="large" />
            )}
          </Avatar>
          <Typography variant="h5" fontWeight="bold">
            {activeTab === 0 ? 'Welcome Back' : 'Create Account'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            {activeTab === 0
              ? 'Sign in to continue your journey'
              : 'Join to unlock full features'}
          </Typography>
        </Box>

        <IconButton
          sx={{ position: 'absolute', top: 8, right: 8, color: 'white' }}
          onClick={onClose}
          disabled={loading || googleLoading}
        >
          <CloseIcon />
        </IconButton>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.9rem',
              py: 2
            }
          }}
        >
          <Tab label="Sign In" />
          <Tab label="Register" />
        </Tabs>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3, pb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
              {error && error.includes("Browser not supported") && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Please use the email/password login option below instead.
                </Typography>
              )}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoogleSignIn}
            disabled={loading || googleLoading}
            sx={{
              py: 1.2,
              mb: 3,
              borderRadius: 2,
              position: 'relative'
            }}
            startIcon={
              googleLoading ? null : <GoogleIcon sx={{ color: '#4285F4' }} />
            }
          >
            {googleLoading ? (
              <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
            ) : (
              'Continue with Google'
            )}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
              OR
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          {/* Email/Password Login Section */}
          <Box sx={{ mt: 1, mb: 3 }}>
            <Typography variant="subtitle2" color="primary" align="center" sx={{ mb: 2 }}>
              Email login works on all browsers and devices
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            sx={{ mb: 2.5 }}
            InputProps={{
              startAdornment: <EmailIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete={
              activeTab === 0 ? 'current-password' : 'new-password'
            }
            sx={{ mb: activeTab === 1 ? 2.5 : 1 }}
            InputProps={{
              startAdornment: <LockIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
            }}
          />

          {activeTab === 1 && (
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <LockIcon color="action" sx={{ mr: 1, fontSize: 20 }} />
                )
              }}
            />
          )}

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Forgot Password?
              </Typography>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ py: 1.5, mt: activeTab === 1 ? 3 : 0, borderRadius: 2, fontWeight: 600 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : activeTab === 0 ? (
              'Sign In'
            ) : (
              'Create Account'
            )}
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ mt: 3, display: 'block' }}
          >
            By continuing, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </DialogContent>
      </form>
    </Dialog>
  );
}